import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/common/PageHeader";
import { cn } from "@/lib/utils";
import StartHereChecklist from "./components/StartHereChecklist";
import ArticleLibrary from "./components/ArticleLibrary";
import PlaybookLibrary from "./components/PlaybookLibrary";
import TutorTabPanel from "./components/TutorTabPanel";

const validTabs = ["start", "library", "playbooks", "tutor"] as const;
type LearnTab = (typeof validTabs)[number];

interface TabDef {
  key: LearnTab;
  label: string;
}

const TABS: TabDef[] = [
  { key: "start", label: "Start Here" },
  { key: "library", label: "Library" },
  { key: "playbooks", label: "Playbooks" },
  { key: "tutor", label: "Genie Tutor" },
];

const LearnHubPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") as LearnTab | null;
  const activeTab: LearnTab = rawTab && validTabs.includes(rawTab) ? rawTab : "start";

  const handleTabChange = (value: LearnTab) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFC] overflow-x-hidden">
      <div className="container mx-auto px-4 py-6 max-w-[1920px]">
        <PageHeader
          title="Learn"
          genieIcon="book-open"
          subtitle="Everything you need to master Help Genie — short guides, branded playbooks, and your own Tutor Genie standing by."
        />

        <div className="mt-4">
          <div className="mb-4">
            <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden">
              <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-min" aria-label="Tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={cn(
                      "whitespace-nowrap py-2.5 sm:py-3 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors",
                      activeTab === tab.key
                        ? "border-genie-primary text-genie-primary"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {activeTab === "start" && <StartHereChecklist />}
          {activeTab === "library" && <ArticleLibrary />}
          {activeTab === "playbooks" && <PlaybookLibrary />}
          {activeTab === "tutor" && <TutorTabPanel />}
        </div>
      </div>
    </div>
  );
};

export default LearnHubPage;
